import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatDate, formatHoursMinutes } from '../../lib/format';
import MultiSelectFilter from '../../components/workspace/MultiSelectFilter';
import { useReportScope } from '../../lib/useReportScope';
import ReportPdfButton, { ExcelIcon, PdfIcon, iconBtnBase, excelBtnStyle, downloadExcel } from '../../components/workspace/ReportPdfButton';

const STATUS_LABEL = {
  open:        'Not Started',
  in_progress: 'In Progress',
  blocked:     'Blocked',
  done:        'Completed',
  cancelled:   'Cancelled',
};

const STATUS_COLOR = {
  open:        '#6b7280',
  in_progress: '#3b82f6',
  blocked:     '#ef4444',
  done:        '#10b981',
  cancelled:   '#9ca3af',
};

const PRIORITY_COLOR = {
  low:      '#6b7280',
  medium:   '#f59e0b',
  high:     '#ef4444',
  urgent:   '#dc2626',
  critical: '#dc2626',
};

const PRIORITY_LABEL = {
  low:      'Low',
  medium:   'Medium',
  high:     'High',
  urgent:   'Urgent',
  critical: 'Urgent',
};


// ── Shared helpers ────────────────────────────────────────────────────────────
const labelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function StatusBadge({ status }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: `${STATUS_COLOR[status] || '#6b7280'}22`,
      color: STATUS_COLOR[status] || '#6b7280',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px',
      textAlign: right ? 'right' : 'left',
      fontWeight: 600,
      color: 'var(--text-secondary)',
      whiteSpace: 'nowrap',
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function TaskReports() {
  const { appId } = useParams();
  const { isAdmin, scopeIds, isManager } = useReportScope(appId);

  const [activeTab, setActiveTab] = useState('hours');

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Task Reports</h1>
          <p className="tl-subtitle">Analyse time logs and task details across the workspace.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="setup-tabs" role="tablist" style={{ marginBottom: 0 }}>
        {[
          { key: 'hours',   label: 'Task Hours Report'   },
          { key: 'details', label: 'Task Details Report' },
        ].map(tab => (
          <button
            key={tab.key}
            role="tab"
            className={`setup-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 24 }}>
        {activeTab === 'hours'   && <TaskHoursTab   appId={appId} isAdmin={isAdmin} scopeIds={scopeIds} isManager={isManager} />}
        {activeTab === 'details' && <TaskDetailsTab appId={appId} isAdmin={isAdmin} scopeIds={scopeIds} isManager={isManager} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 1 — Task Hours Report
// ═══════════════════════════════════════════════════════════════════════════════
function TaskHoursTab({ appId, isAdmin, scopeIds, isManager }) {
  const api = useApi();
  const { me } = useWorkspace();
  const isMultiUser = isAdmin || isManager;

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 7) + '-01';
  const [fromDate, setFromDate]       = useState(firstOfMonth);
  const [toDate,   setToDate]         = useState(today);
  const [filterMembers, setFilterMembers] = useState([]);
  const [filterStatus,  setFilterStatus]  = useState('');
  const [members, setMembers]             = useState([]);
  const [items, setItems]                 = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);

  useEffect(() => {
    api.listAppMembers(appId)
      .then(r => setMembers(r.items || []))
      .catch(() => {});
  }, [appId]);

  const loadReport = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.taskTimeLogReport(appId, { from: fromDate, to: toDate });
      setItems(res.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [appId, fromDate, toDate]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const nameMap = useMemo(() => {
    const map = {};
    members.forEach(m => {
      map[m.user_id] = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    });
    if (me) map[me.id] = [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email;
    return map;
  }, [members, me]);

  const memberName = id => (id == null ? 'Unassigned' : nameMap[id] || `User #${id}`);

  const filteredItems = useMemo(() => {
    let result = items;
    if (isMultiUser && filterMembers.length > 0)
      result = result.filter(r => filterMembers.includes(String(r.logger_user_id)));
    return result;
  }, [items, filterMembers, isMultiUser]);

  const taskGroups = useMemo(() => {
    const groups = [];
    let current  = null;
    for (const row of filteredItems) {
      if (!current || current.task_id !== row.task_id) {
        current = {
          task_id:             row.task_id,
          task_number:         row.task_number,
          title:               row.title,
          status:              row.status,
          assigned_to_user_id: row.assigned_to_user_id,
          rows:                [],
          total_minutes:       0,
        };
        groups.push(current);
      }
      current.rows.push(row);
      current.total_minutes += row.user_minutes;
    }
    return groups;
  }, [filteredItems]);

  const displayGroups = useMemo(() => {
    if (!filterStatus) return taskGroups;
    return taskGroups.filter(g => g.status === filterStatus);
  }, [taskGroups, filterStatus]);

  const grandTotalMinutes = useMemo(
    () => displayGroups.reduce((s, g) => s + g.total_minutes, 0),
    [displayGroups]
  );
  const grandTotalHours = formatHoursMinutes(grandTotalMinutes);

  const uniqueMemberCount = useMemo(
    () => [...new Set(displayGroups.flatMap(g => g.rows.map(r => r.logger_user_id)))].length,
    [displayGroups]
  );

  const isAllView = isMultiUser;

  const excelHeaders = ['S.No', 'Task', 'Task Title', 'Status', ...(isAllView ? ['Assignee'] : []), 'First Log', 'Last Log', 'Hours'];
  const excelRows = useMemo(() => displayGroups.flatMap((group, gi) =>
    group.rows.map((row, ri) => [
      ri === 0 ? gi + 1 : '',
      ri === 0 ? (row.task_number || `#${row.task_id}`) : '',
      ri === 0 ? group.title : '',
      ri === 0 ? (STATUS_LABEL[group.status] || group.status) : '',
      ...(isAllView ? [memberName(row.logger_user_id)] : []),
      row.first_log_date ? formatDate(row.first_log_date) : '',
      row.last_log_date  ? formatDate(row.last_log_date)  : '',
      formatHoursMinutes(row.user_minutes),
    ])
  ), [displayGroups, isAllView]);

  const pdfHeaders = ['S.No', 'Task', 'Task Title', 'Status', ...(isAllView ? ['Assignee'] : []), 'First Log', 'Last Log', 'Hours'];
  const pdfRows = useMemo(() => displayGroups.flatMap((group, gi) =>
    group.rows.map((row, ri) => [
      ri === 0 ? gi + 1 : '',
      ri === 0 ? (row.task_number || `#${row.task_id}`) : '',
      ri === 0 ? group.title : '',
      ri === 0 ? (STATUS_LABEL[group.status] || group.status) : '',
      ...(isAllView ? [memberName(row.logger_user_id)] : []),
      row.first_log_date ? formatDate(row.first_log_date) : '',
      row.last_log_date  ? formatDate(row.last_log_date)  : '',
      formatHoursMinutes(row.user_minutes),
    ])
  ), [displayGroups, isAllView]);


  const pdfTable = useMemo(() => ({
    headers: pdfHeaders,
    rows: pdfRows,
  }), [pdfHeaders, pdfRows]);

  const periodLabel = fromDate === toDate ? fromDate : `${fromDate} – ${toDate}`;
  const pdfCards = useMemo(() => !loading && displayGroups.length > 0 ? [
    { label: 'Tasks',       value: displayGroups.length,   color: 'var(--text-primary)' },
    { label: 'Total Hours', value: grandTotalHours,  color: 'var(--brand)'        },
    ...(isAllView ? [{ label: 'Members', value: uniqueMemberCount, color: '#6f7bff' }] : []),
    { label: 'Period',      value: periodLabel,             color: '#4ad295'             },
  ] : [], [loading, displayGroups.length, grandTotalHours, isAllView, uniqueMemberCount, periodLabel]);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
          Hours are attributed to whoever <strong>logged the time</strong> — not the current assignee.
          Previous assignees' work always appears in their own report.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadExcel(
              `task-hours-${fromDate}-${toDate}.xls`,
              'Task Hours Report',
              `Period: ${periodLabel}${filterStatus ? `  |  Status: ${STATUS_LABEL[filterStatus]}` : ''}`,
              excelHeaders,
              [...excelRows, ['', '', '', ...(isAllView ? [''] : []), '', 'Grand Total', grandTotalHours]]
            )}
            disabled={displayGroups.length === 0}
            style={{ ...iconBtnBase, background: '#f0faf4', color: '#166534', borderColor: '#bbf7d0', opacity: displayGroups.length === 0 ? 0.4 : 1 }}
          >
            <ExcelIcon /> Excel
          </button>
          <ReportPdfButton
            title="Task Hours Report"
            subtitle={`Period: ${periodLabel}`}
            summaryCards={pdfCards}
            table={pdfTable}
            icon={<PdfIcon />}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24,
        padding: '16px 20px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>From</label>
          <input
            type="date"
            className="tl-select"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            style={{ minWidth: 150 }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>To</label>
          <input
            type="date"
            className="tl-select"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            style={{ minWidth: 150 }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Status</label>
          <select className="tl-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {isMultiUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={labelStyle}>Team Member</label>
            <MultiSelectFilter
              options={members
                .filter(m => !scopeIds || scopeIds.has(String(m.user_id)))
                .map(m => ({ value: String(m.user_id), label: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email }))}
              selected={filterMembers}
              onChange={setFilterMembers}
              placeholder="All Members"
              minWidth={180}
            />
          </div>
        )}

        {!isMultiUser && me && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={labelStyle}>Member</label>
            <div style={{ padding: '6px 12px', background: 'var(--bg-hover, rgba(255,255,255,0.05))',
              border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 14 }}>
              {memberName(me.id)}
            </div>
          </div>
        )}
      </div>

      {/* Summary cards */}
      {!loading && displayGroups.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Tasks',       value: displayGroups.length },
            { label: 'Total Hours', value: grandTotalHours },
            ...(isAllView ? [{ label: 'Members', value: uniqueMemberCount }] : []),
            { label: 'Period',      value: periodLabel },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 120 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : displayGroups.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)',
          background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No time logs found for {periodLabel}{filterStatus ? ` with status "${STATUS_LABEL[filterStatus]}"` : ''}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>S.No</Th>
                <Th>Task</Th>
                <Th>Task Title</Th>
                <Th>Status</Th>
                {isAllView && <Th>Assignee</Th>}
                <Th>First Log</Th>
                <Th>Last Log</Th>
                <Th right>Hours</Th>
              </tr>
            </thead>
            <tbody>
              {displayGroups.map((group, gi) => {
                const groupBg = gi % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)';
                const taskTotalHours = formatHoursMinutes(group.total_minutes);

                return group.rows.map((row, ri) => {
                  const isFirst = ri === 0;
                  const isLast  = ri === group.rows.length - 1;
                  return (
                    <tr key={`${group.task_id}-${row.logger_user_id}`}
                      style={{ background: groupBg,
                        borderBottom: isLast
                          ? '2px solid var(--border)'
                          : '1px solid rgba(255,255,255,0.04)' }}>

                      <td style={{ padding: '10px 14px', verticalAlign: 'top',
                        color: 'var(--text-muted)', fontSize: 12, textAlign: 'center',
                        whiteSpace: 'nowrap' }}>
                        {isFirst ? gi + 1 : ''}
                      </td>

                      <td style={{ padding: '10px 14px', verticalAlign: 'top',
                        color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12,
                        whiteSpace: 'nowrap' }}>
                        {isFirst ? (row.task_number || `#${row.task_id}`) : ''}
                      </td>

                      <td style={{ padding: '10px 14px', verticalAlign: 'top',
                        fontWeight: isFirst ? 600 : 400, color: isFirst ? 'var(--text-primary)' : 'transparent',
                        maxWidth: 260, userSelect: isFirst ? 'auto' : 'none' }}>
                        {isFirst ? group.title : ''}
                        {isLast && group.rows.length > 1 && (
                          <div style={{ marginTop: isFirst ? 4 : 0, fontSize: 11,
                            color: 'var(--text-muted)', fontWeight: 400 }}>
                            Task total: <strong style={{ color: 'var(--brand)' }}>{taskTotalHours}</strong>
                          </div>
                        )}
                      </td>

                      <td style={{ padding: '10px 14px', verticalAlign: 'top' }}>
                        {isFirst && <StatusBadge status={group.status} />}
                      </td>

                      {isAllView && (
                        <td style={{ padding: '10px 14px', color: 'var(--text-primary)',
                          fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {memberName(row.logger_user_id)}
                          {row.logger_user_id === group.assigned_to_user_id && (
                            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600,
                              color: 'var(--brand)', background: 'rgba(var(--brand-rgb,99,102,241),0.12)',
                              padding: '1px 6px', borderRadius: 10 }}>
                              current
                            </span>
                          )}
                        </td>
                      )}

                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {row.first_log_date ? formatDate(row.first_log_date) : '—'}
                      </td>

                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {row.last_log_date ? formatDate(row.last_log_date) : '—'}
                      </td>

                      <td style={{ padding: '10px 14px', fontWeight: 700,
                        color: 'var(--brand)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatHoursMinutes(row.user_minutes)}
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>

            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-card)' }}>
                <td colSpan={isAllView ? 7 : 6}
                  style={{ padding: '10px 14px', fontWeight: 700,
                    color: 'var(--text-secondary)', textAlign: 'right' }}>
                  Grand Total
                </td>
                <td style={{ padding: '10px 14px', fontWeight: 800,
                  color: 'var(--brand)', textAlign: 'right', fontSize: 15 }}>
                  {grandTotalHours}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 2 — Task Details Report
// ═══════════════════════════════════════════════════════════════════════════════
function TaskDetailsTab({ appId, isAdmin, scopeIds, isManager }) {
  const api = useApi();
  const { me } = useWorkspace();
  const isMultiUser = isAdmin || isManager;

  const todayD        = new Date();
  const currentYM     = `${todayD.getFullYear()}-${String(todayD.getMonth() + 1).padStart(2, '0')}`;

  const [filterStatus,    setFilterStatus]    = useState('');
  const [filterPriority,  setFilterPriority]  = useState('');
  const [filterArea,      setFilterArea]      = useState('');
  const [filterAssignee,  setFilterAssignee]  = useState([]);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [fromMonth,       setFromMonth]       = useState(currentMonth);
  const [toMonth,         setToMonth]         = useState(currentMonth);
  const [members,         setMembers]         = useState([]);
  const [items,           setItems]           = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);

  useEffect(() => {
    api.listAppMembers(appId)
      .then(r => setMembers(r.items || []))
      .catch(() => {});
  }, [appId]);

  const nameMap = useMemo(() => {
    const map = {};
    members.forEach(m => {
      map[m.user_id] = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    });
    if (me) map[me.id] = [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email;
    return map;
  }, [members, me]);

  const memberName = id => (id == null ? 'Unassigned' : nameMap[id] || `User #${id}`);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterStatus)   params.status   = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      if (filterArea)     params.area_id  = filterArea;
      if (fromMonth) {
        params.from = `${fromMonth}-01`;
        // to: last day of toMonth (or fromMonth if toMonth not set)
        const [y, m] = (toMonth || fromMonth).split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        params.to = `${toMonth || fromMonth}-${String(lastDay).padStart(2, '0')}`;
      }
      const res = await api.taskDetailsReport(appId, params);
      setItems(res.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [appId, filterStatus, filterPriority, filterArea, fromMonth, toMonth]);

  useEffect(() => { loadReport(); }, [loadReport]);

  // Collect unique area options from loaded data
  const areaOptions = useMemo(() => {
    const seen = new Map();
    for (const t of items) {
      if (t.area_id && !seen.has(t.area_id)) seen.set(t.area_id, t.area_name || `Area #${t.area_id}`);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [items]);

  const filteredTaskItems = useMemo(() => {
    if (filterAssignee.length === 0) return items;
    return items.filter(t => filterAssignee.includes(String(t.assigned_to_user_id)));
  }, [items, filterAssignee]);

  const totalTasks  = filteredTaskItems.length;
  const totalHours  = useMemo(() => formatHoursMinutes(filteredTaskItems.reduce((s, r) => s + Number(r.total_minutes), 0)), [filteredTaskItems]);
  const doneTasks   = filteredTaskItems.filter(t => t.status === 'done').length;

  const detailsExcelHeaders = ['S.No', 'Task', 'Title', 'Status', 'Priority', 'Creator', 'Assignee', 'Previous Assignees', 'Created', 'Deadline', 'Est. h', 'Logged h', 'Checklist'];
  const detailsExcelRows = useMemo(() => filteredTaskItems.map((task, i) => {
    const prevNames = task.previous_assignee_ids
      .filter(id => id !== task.assigned_to_user_id)
      .map(id => memberName(id));
    const clTotal = task.checklist_total;
    const clDone  = task.checklist_done;
    return [
      i + 1,
      task.task_number || `#${task.id}`,
      task.title,
      STATUS_LABEL[task.status] || task.status,
      PRIORITY_LABEL[task.priority] || task.priority || '',
      memberName(task.created_by_user_id),
      memberName(task.assigned_to_user_id),
      prevNames.join(', ') || '',
      task.created_at ? formatDate(task.created_at.slice(0, 10)) : '',
      task.due_on     ? formatDate(task.due_on)                  : '',
      task.estimated_minutes > 0 ? formatHoursMinutes(task.estimated_minutes) : '',
      task.total_minutes     > 0 ? formatHoursMinutes(task.total_minutes)     : '',
      clTotal === 0 ? '' : `${clDone}/${clTotal}`,
    ];
  }), [filteredTaskItems]);

  const pdfTable = useMemo(() => ({
    headers: detailsExcelHeaders,
    rows: detailsExcelRows,
  }), [detailsExcelRows]);

  const pdfCards = useMemo(() => !loading && filteredTaskItems.length > 0 ? [
    { label: 'Total Tasks', value: totalTasks,         color: 'var(--text-primary)' },
    { label: 'Completed',   value: doneTasks,          color: '#4ad295'             },
    { label: 'Total Hours', value: totalHours,   color: 'var(--brand)'        },
  ] : [], [loading, filteredTaskItems.length, totalTasks, doneTasks, totalHours]);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
          Full task overview — status, priority, area, assignee history, hours logged, and checklist progress.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => {
              const sub = [
                fromMonth && `${fromMonth}${toMonth && toMonth !== fromMonth ? ` – ${toMonth}` : ''}`,
                filterStatus && `Status: ${STATUS_LABEL[filterStatus]}`,
                filterPriority && `Priority: ${filterPriority}`,
              ].filter(Boolean).join('  |  ') || 'All Tasks';
              downloadExcel(`task-details.xls`, 'Task Details Report', sub, detailsExcelHeaders, detailsExcelRows);
            }}
            disabled={filteredTaskItems.length === 0}
            style={{ ...iconBtnBase, background: '#f0faf4', color: '#166534', borderColor: '#bbf7d0', opacity: filteredTaskItems.length === 0 ? 0.4 : 1 }}
          >
            <ExcelIcon /> Excel
          </button>
          <ReportPdfButton
            title="Task Details Report"
            subtitle={[
              fromMonth && `${fromMonth}${toMonth && toMonth !== fromMonth ? ` – ${toMonth}` : ''}`,
              filterStatus && `Status: ${STATUS_LABEL[filterStatus]}`,
              filterPriority && `Priority: ${filterPriority}`,
            ].filter(Boolean).join(' · ') || 'All Tasks'}
            summaryCards={pdfCards}
            table={pdfTable}
            columnStyles={{ 2: { cellWidth: 55 } }}
            wrapCols={[2]}
            icon={<PdfIcon />}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24,
        padding: '16px 20px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>From Month</label>
          <input
            type="month"
            className="tl-select"
            value={fromMonth}
            onChange={e => setFromMonth(e.target.value)}
            style={{ minWidth: 150 }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>To Month</label>
          <input
            type="month"
            className="tl-select"
            value={toMonth}
            min={fromMonth || undefined}
            onChange={e => setToMonth(e.target.value)}
            style={{ minWidth: 150 }}
          />
        </div>

        {(fromMonth || toMonth) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setFromMonth(''); setToMonth(''); }}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13 }}
            >
              Clear
            </button>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Status</label>
          <select className="tl-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Priority</label>
          <select className="tl-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        {areaOptions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={labelStyle}>Area</label>
            <select className="tl-select" value={filterArea} onChange={e => setFilterArea(e.target.value)} style={{ minWidth: 160 }}>
              <option value="">All Areas</option>
              {areaOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              <option value="others">No Area</option>
            </select>
          </div>
        )}

        {members.length > 0 && isMultiUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={labelStyle}>Assignee</label>
            <MultiSelectFilter
              options={members
                .filter(m => !scopeIds || scopeIds.has(String(m.user_id)))
                .map(m => ({ value: String(m.user_id), label: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email }))}
              selected={filterAssignee}
              onChange={setFilterAssignee}
              placeholder="All Assignees"
              minWidth={180}
            />
          </div>
        )}
      </div>

      {/* Summary cards */}
      {!loading && filteredTaskItems.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Tasks',  value: totalTasks },
            { label: 'Completed',    value: doneTasks },
            { label: 'Total Hours',  value: totalHours },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 120 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)',
          background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No tasks found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>S.No</Th>
                <Th>Task</Th>
                <Th>Title</Th>
                <Th>Status</Th>
                <Th>Priority</Th>
                <Th>Creator</Th>
                <Th>Current Assignee</Th>
                <Th>Previous Assignees</Th>
                <Th>Created</Th>
                <Th>Deadline</Th>
                <Th right>Est. h</Th>
                <Th right>Logged h</Th>
                <Th>Checklist</Th>
              </tr>
            </thead>
            <tbody>
              {filteredTaskItems.map((task, i) => {
                const rowBg = i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)';
                const prevNames = task.previous_assignee_ids
                  .filter(id => id !== task.assigned_to_user_id)
                  .map(id => memberName(id));

                const clTotal = task.checklist_total;
                const clDone  = task.checklist_done;
                const clPct   = clTotal === 0 ? null : Math.round((clDone / clTotal) * 100);

                return (
                  <tr key={task.id} style={{ background: rowBg, borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)',
                      fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {i + 1}
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)',
                      fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>
                      {task.task_number || `#${task.id}`}
                    </td>

                    <td style={{ padding: '10px 14px', fontWeight: 600,
                      color: 'var(--text-primary)', maxWidth: 240 }}>
                      {task.title}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <StatusBadge status={task.status} />
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                        fontSize: 11, fontWeight: 600,
                        background: `${PRIORITY_COLOR[task.priority] || '#6b7280'}22`,
                        color: PRIORITY_COLOR[task.priority] || '#6b7280',
                      }}>
                        {PRIORITY_LABEL[task.priority] || task.priority || '—'}
                      </span>
                    </td>

                    <td style={{ padding: '10px 14px', color: 'var(--text-primary)',
                      fontSize: 12, whiteSpace: 'nowrap' }}>
                      {memberName(task.created_by_user_id)}
                    </td>

                    <td style={{ padding: '10px 14px', color: 'var(--text-primary)',
                      fontWeight: 500, whiteSpace: 'nowrap' }}>
                      {memberName(task.assigned_to_user_id)}
                    </td>

                    <td style={{ padding: '10px 14px', fontSize: 12 }}>
                      {prevNames.length === 0 ? (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {prevNames.map((n, idx) => (
                            <span key={idx} style={{
                              padding: '1px 7px', borderRadius: 12,
                              background: 'rgba(255,255,255,0.06)',
                              border: '1px solid var(--border)',
                              color: 'var(--text-secondary)',
                              fontSize: 11, whiteSpace: 'nowrap',
                            }}>
                              {n}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>

                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)',
                      fontSize: 12, whiteSpace: 'nowrap' }}>
                      {task.created_at ? formatDate(task.created_at.slice(0, 10)) : '—'}
                    </td>

                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)',
                      fontSize: 12, whiteSpace: 'nowrap' }}>
                      {task.due_on ? formatDate(task.due_on) : '—'}
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'right',
                      color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {task.estimated_minutes > 0 ? formatHoursMinutes(task.estimated_minutes) : '—'}
                    </td>

                    <td style={{ padding: '10px 14px', textAlign: 'right',
                      fontWeight: 700, color: 'var(--brand)', whiteSpace: 'nowrap' }}>
                      {task.total_minutes > 0 ? formatHoursMinutes(task.total_minutes) : '—'}
                    </td>

                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {clTotal === 0 ? (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 80 }}>
                          <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.08)',
                            borderRadius: 3, overflow: 'hidden', minWidth: 40 }}>
                            <div style={{
                              height: '100%', borderRadius: 3,
                              width: `${clPct}%`,
                              background: clPct === 100 ? '#10b981' : 'var(--brand)',
                            }} />
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                            {clDone}/{clTotal}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
